import { useId } from 'react';

type FalconProps = {
  className?: string;
};

export function Falcon({ className }: FalconProps) {
  const uid = useId().replace(/:/g, '');
  const hullId = `${uid}-hull`;
  const shadowId = `${uid}-shadow`;
  const dishId = `${uid}-dish`;
  const glassId = `${uid}-glass`;
  const gapMaskId = `${uid}-gap-mask`;

  return (
    <svg className={className} viewBox="0 0 720 420" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id={hullId} cx="38%" cy="28%" r="76%">
          <stop offset="0%" stopColor="#d5dce2" />
          <stop offset="48%" stopColor="#8996a2" />
          <stop offset="100%" stopColor="#39444f" />
        </radialGradient>
        <linearGradient id={shadowId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#64717d" />
          <stop offset="100%" stopColor="#202831" />
        </linearGradient>
        <radialGradient id={dishId} cx="36%" cy="32%" r="68%">
          <stop offset="0%" stopColor="#b9c4cd" />
          <stop offset="100%" stopColor="#46515c" />
        </radialGradient>
        <radialGradient id={glassId} cx="30%" cy="28%" r="70%">
          <stop offset="0%" stopColor="#8ec6ff" />
          <stop offset="48%" stopColor="#345b78" />
          <stop offset="100%" stopColor="#111b24" />
        </radialGradient>
        <mask id={gapMaskId}>
          <rect width="720" height="420" fill="white" />
          <path d="M372 194 700 184 700 226 435 244 380 229Z" fill="black" />
        </mask>
      </defs>

      {/* Thick lower hull establishes the same dimensional language as the Death Star. */}
      <path
        d="M67 176C96 266 203 326 352 328c139 1 235-45 270-111l-15 46c-47 72-143 108-258 107-143-2-249-57-294-143Z"
        fill={`url(#${shadowId})`}
        mask={`url(#${gapMaskId})`}
      />

      {/* The Falcon's saucer is interrupted by its two forward freight mandibles. */}
      <path
        d="M66 176C84 91 183 35 326 33c139-2 246 54 288 132l-129 10-57 30-42-7-31-33-34 25-53 5-59-24Z"
        fill={`url(#${hullId})`}
        mask={`url(#${gapMaskId})`}
      />
      <path
        d="M66 176C84 91 183 35 326 33c139-2 246 54 288 132l-129 10-57 30-42-7-31-33-34 25-53 5-59-24Z"
        stroke="rgba(16,32,51,0.42)"
        strokeWidth="2"
        mask={`url(#${gapMaskId})`}
      />

      {/* Parallel mandibles project forward horizontally around a deep central freight channel. */}
      <path d="M355 157 450 160 665 150 680 174 665 198 430 210 379 198Z" fill={`url(#${hullId})`} />
      <path d="M390 225 435 238 665 215 680 240 655 274 440 286 350 247Z" fill={`url(#${hullId})`} />
      <path
        d="M355 157 450 160 665 150 680 174 665 198 430 210 379 198Z"
        stroke="rgba(16,32,51,0.38)"
        strokeWidth="2"
      />
      <path
        d="M390 225 435 238 665 215 680 240 655 274 440 286 350 247Z"
        stroke="rgba(16,32,51,0.38)"
        strokeWidth="2"
      />

      <path d="M440 286 655 274 638 293 451 304 350 247Z" fill="#26313b" />

      {/* The Falcon's blue-white sublight engine follows the curved rear hull. */}
      <path
        d="M106 91C64 122 50 167 62 210c10 39 41 73 88 96"
        stroke="#202831"
        strokeWidth="18"
        strokeLinecap="round"
      />
      <path
        d="M106 91C64 122 50 167 62 210c10 39 41 73 88 96"
        stroke="#4ca7ff"
        strokeWidth="10"
        strokeLinecap="round"
        opacity="0.86"
      />
      <path d="M106 91C64 122 50 167 62 210c10 39 41 73 88 96" stroke="#e8f7ff" strokeWidth="3" strokeLinecap="round" />

      {/* Concentric armor bands and radial seams sell the Corellian freighter hull. */}
      <ellipse
        cx="320"
        cy="180"
        rx="197"
        ry="139"
        stroke="rgba(238,243,248,0.11)"
        strokeWidth="1.3"
        mask={`url(#${gapMaskId})`}
      />
      <ellipse
        cx="320"
        cy="180"
        rx="151"
        ry="103"
        stroke="rgba(16,32,51,0.18)"
        strokeWidth="1.5"
        mask={`url(#${gapMaskId})`}
      />
      <path
        d="M320 43v108M170 85l109 76M106 161l159 17M160 267l122-65M274 317l28-110M425 292l-83-91M494 104l-143 69"
        stroke="rgba(16,32,51,0.2)"
        strokeWidth="1.3"
        mask={`url(#${gapMaskId})`}
      />
      <path
        d="M145 126h52v12h-52zM187 238h51v13h-51zM242 73h55v11h-55zM366 72h47v12h-47z"
        fill="#6d3b34"
        opacity="0.9"
      />
      <path d="M267 247h42v12h-42zM442 121h38v10h-38z" fill="#9b814f" opacity="0.72" />

      {/* Six rear heat vents: the fastest read after the mandibles. */}
      {[0, 1, 2].map((column) =>
        [0, 1].map((row) => {
          const x = 173 + column * 42;
          const y = 112 + row * 39;
          return (
            <g key={`${column}-${row}`}>
              <ellipse cx={x} cy={y} rx="17" ry="12" fill="#222a31" stroke="#9aa6b2" strokeWidth="2" />
              <path
                d={`M${x - 11} ${y - 5}h22M${x - 13} ${y}h26M${x - 11} ${y + 5}h22`}
                stroke="#53616d"
                strokeWidth="1.5"
              />
            </g>
          );
        }),
      )}

      {/* Central quad-laser turret. */}
      <ellipse cx="333" cy="177" rx="34" ry="27" fill="#87939e" stroke="#303a44" strokeWidth="2" />
      <ellipse cx="333" cy="177" rx="18" ry="14" fill="#303a44" />
      <path d="m322 166 22 22m0-22-22 22" stroke="#aeb8c0" strokeWidth="2" />
      <path d="M330 163v-13m7 13v-13" stroke="#303a44" strokeWidth="3" />

      {/* Original trilogy sensor dish. */}
      <ellipse cx="438" cy="121" rx="34" ry="25" fill={`url(#${dishId})`} stroke="#313b45" strokeWidth="2" />
      <path
        d="M412 113c18 12 35 17 54 16M438 97v47M419 103l38 35M457 103l-38 35"
        stroke="rgba(238,243,248,0.24)"
        strokeWidth="1.3"
      />
      <path d="m438 121 18-13" stroke="#303a44" strokeWidth="3" />
      <circle cx="438" cy="121" r="4" fill="#8aa6bc" />

      {/* Offset cockpit tube and faceted canopy. */}
      <path
        d="M174 255c34 8 67 27 91 51l-25 35c-28-24-58-41-94-49Z"
        fill={`url(#${hullId})`}
        stroke="#303a44"
        strokeWidth="2"
      />
      <path
        d="m240 341 23-34c22 8 38 22 43 40-10 22-27 34-47 35-13-10-19-24-19-41Z"
        fill={`url(#${glassId})`}
        stroke="#b8c3cc"
        strokeWidth="2"
      />
      <path d="m247 329 52 28m-39-43-1 67m18-60-18 60m-19-40 66 6" stroke="#cbd5dd" strokeWidth="1.4" opacity="0.85" />

      {/* Sparse greebles keep the graphic restrained but mechanical. */}
      <path
        d="M376 112h42v8h-42zM381 128h24v6h-24zM474 195h35v7h-35zM500 211h21v6h-21zM236 214h42v7h-42z"
        fill="#303a44"
        opacity="0.8"
      />
      <path
        d="m394 91 23 13m-33-3 28 16m81 70 38 8m-46 3 38 8m-266 9 35 11"
        stroke="rgba(238,243,248,0.18)"
        strokeWidth="2"
      />
    </svg>
  );
}
